import { defineStore } from 'pinia'
import { NAV_HISTORY_KEY } from '@/router/backRoute'
import { useOrganizationStore } from '@/store/pinia/organization'
import { decodeJWT } from '@/utils/helpers'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction, createPersistedState, VERSION_KEY } from '@/utils/helpers/pinia'
import { checkEntityCanonicalMatch } from '@/utils/plugins/permissions'
import { useLicenceStore } from './organization/licence'
import { useRootStore } from './root'

const { VUE_APP_DEFAULT_ROUTE_NAME_REDIRECT } = import.meta.env
const VERSION = 1
const EVALUATED_ACTIONS_TTL_MS = 2 * 60 * 1000

export function isTokenExpired (jwt) {
  let payload

  if (isTokenExpired.cachedToken === jwt) {
    payload = isTokenExpired.cachedPayload
  } else {
    const encPayload = jwt.split('.')[1]
    if (!encPayload) {
      throw new Error(`Invalid JWT token: ${jwt}`)
    }

    try {
      payload = decodeJWT(encPayload)
    } catch (error) {
      throw new Error(`Invalid payload in JWT token: ${jwt}. Original Error: ${error.message}`)
    }

    isTokenExpired.cachedToken = jwt
    isTokenExpired.cachedPayload = payload
  }

  return (payload.exp * 1000 - Date.now()) <= 0
}

export function initializeAfterLogInRedirectRoute () {
  const route = localStorage.getItem(LSK.REDIRECT_ROUTE)
  return !route
    ? { name: VUE_APP_DEFAULT_ROUTE_NAME_REDIRECT }
    : JSON.parse(route)
}

// Seed the owned-projects list from localStorage so it survives a reload: it is only pushed by
// login/refresh, which don't run on a cold boot, and the owner fallback (BE-473) relies on it.
export function initializeOwns () {
  const owns = localStorage.getItem(LSK.OWNS)
  return owns ? JSON.parse(owns) : null
}

/**
 * The resource of a component policy is a path of its own
 * (organization:project:environment:component), so a component action is evaluated against one
 * canonical per level, in that order, and the levels the route does not know are wildcarded -
 * creating a component has no component canonical yet, and a project page has no environment.
 * Returns null when even the project is unknown, so the action is evaluated organization wide.
 *
 * @param {Object} canonicals - the route params
 * @returns {string[]|null}
 */
function getComponentPath ({ projectCanonical, envCanonical, componentCanonical }) {
  if (_.isEmpty(projectCanonical)) return null
  return [projectCanonical, envCanonical || '*', componentCanonical || '*']
}

function getActionCanonical (action, canonicals = {}) {
  const componentActions = ['CreateComponent', 'GetComponent', 'UpdateComponent', 'DeleteComponent', 'ListComponents']
  if (_.includes(componentActions, action)) return getComponentPath(canonicals)

  return {
    UpdatePipeline: canonicals.pipelineCanonical,
    UpdateProject: canonicals.projectCanonical,
  }?.[action]
}

function toEntityCanonicals (canonical) {
  // A component path is positional, so it keeps the order it was built in; every other action
  // takes a set of canonicals, where the order carries no meaning
  if (_.isArray(canonical)) return _.compact(canonical)
  return _.isEmpty(canonical) ? [] : [canonical]
}

function getEvaluationKey (action, entityCanonicals = []) {
  return `${action}|${entityCanonicals.join(',')}`
}

function isCacheEntryFresh (entry) {
  if (!entry?.fetchedAt) return false
  return Date.now() - entry.fetchedAt < EVALUATED_ACTIONS_TTL_MS
}

function doesEntryCoverCanonical (entry, canonical) {
  // A path is answered for that exact path. can_do strips the resources it returns down to
  // their last level, so nothing in the response can tell whether another project or
  // environment is covered - only re-evaluating can
  if (_.isArray(canonical)) return _.isEqual(entry.requestedCanonicals, toEntityCanonicals(canonical))
  if (_.isEmpty(canonical) || _.isEmpty(entry.entityCanonicals)) return !!entry.ok
  return checkEntityCanonicalMatch(entry.entityCanonicals, canonical)
}

function buildRefreshTokenQuery (query, scopes, currentScopeDepth) {
  if (!_.$isEmpty(query)) return query
  const orgCanonical = _.$get(scopes, '[0].canonical', null)
  const childCanonical = _.$get(scopes[currentScopeDepth], 'canonical')
  return {
    ...(orgCanonical ? { organization_canonical: orgCanonical } : {}),
    ...(childCanonical && childCanonical !== orgCanonical ? { child_canonical: childCanonical } : {}),
  }
}

export const state = () => ({
  [VERSION_KEY]: VERSION,
  /** @type {string} */
  jwt: null,
  invitation: {
    email: null,
    token: null,
  },
  verified: false,
  permissions: null,
  owns: initializeOwns(),
  evaluatedActions: {},
  _evaluatedActionsInFlight: {},
  _evaluatedActionsPending: [],
  _evaluatedActionsFlushHandle: null,
  _orgSwitchInFlight: null,
  isEvaluatingUserActions: false,
  _evaluationsInFlight: 0,
  awsMarketplaceToken: null,
  awsMarketplaceSubscription: null,
  afterLogIn: {
    redirectRoute: initializeAfterLogInRedirectRoute(),
  },
  errors: {
    sso: [],
    signUp: [],
    login: [],
    impersonate: [],
    verifyEmail: [],
    resetPassword: [],
  },
  sso: {},
  socialProvider: '',
})

export const useAuthStore = defineStore('auth', {
  state,

  getters: {
    defaultRoute: (state) => {
      const orgStore = useOrganizationStore()
      return _.merge({
        ...state.afterLogIn.redirectRoute,
        params: {
          ...state.afterLogIn.redirectRoute?.params,
          orgCanonical: orgStore.orgCanonical,
        },
      })
    },
    isAuthenticated: (state) => !!state.jwt && !isTokenExpired(state.jwt),
    isOrgAdmin: (state) => _.keys(state?.permissions ?? {}).includes('organization:**'),
    jwtDecoded: (state) => !_.isNull(state.jwt)
      ? decodeJWT(state.jwt.split('.')[1]).cycloid
      : null,
    ssoUserProfile: ({ sso }) => sso?.user
      ? _.pick(sso.user, ['email', 'full_name', 'username', 'social_id'])
      : {},
    username () {
      return this.jwtDecoded?.user.username ?? null
    },
    /** Canonical of the support user behind an impersonated session (ENG-407), null otherwise */
    impersonator () {
      return this.jwtDecoded?.impersonator ?? null
    },
    isImpersonating () {
      return !_.isNull(this.impersonator)
    },
  },

  actions: {
    CLEAR_AUTH_ERRORS: makeClearErrorsAction(state),
    RESET_AUTH_STATE: makeResetStoreAction(state),
    SET_ERRORS: makeSetErrorsAction(),

    async REFRESH_TOKEN ({ query } = {}) {
      if (!this.isAuthenticated) return
      if (_.$isEmpty(query) && this._orgSwitchInFlight) return this._orgSwitchInFlight

      const { useCustomersStore } = await import('@/store/pinia/customers')
      const customersStore = useCustomersStore()
      const refreshTokenQuery = buildRefreshTokenQuery(query, customersStore.scopes, customersStore.currentScopeDepth)

      if (_.$isEmpty(refreshTokenQuery)) return

      const { data, errors } = (await this.$cycloid.ydAPI.refreshToken(refreshTokenQuery)) || {}

      if (data) {
        const prevPermissions = JSON.stringify(this.permissions || {})
        this.SET_CREDENTIALS(data)
        const nextPermissions = JSON.stringify(this.permissions || {})
        if (prevPermissions !== nextPermissions) this.CLEAR_EVALUATED_ACTIONS_CACHE()
      }
      if (errors) {
        if (errors[0].code === 'RequireOrganizationMFA') return 'MFAError'
        // Clear synchronously: the logout navigation below is fire-and-forget and only resolves on
        // the next router transition, so callers gating on "is jwt still truthy" (ENG-307) would
        // otherwise see the stale-but-valid previous token and wrongly treat the refresh as having
        // succeeded.
        this.CLEAR_CREDENTIALS()
        this.$router.push({ name: 'logout' }).catch(() => { /* silenced */ })
      }
    },

    async EVALUATE_USER_ACTIONS ({ actions: Actions = null, canonicals }) {
      const { isAuthenticated, isOrgAdmin, jwtDecoded } = this
      const orgCanonical = _.$get(jwtDecoded, 'organization.canonical')

      if (_.some([!Actions, !isAuthenticated, !orgCanonical, isOrgAdmin])) return

      const uniqueActions = _.uniq(Actions)

      this._evaluationsInFlight += 1
      this.isEvaluatingUserActions = true
      try {
        await Promise.all(uniqueActions.map((action) => {
          const canonical = getActionCanonical(action, canonicals)
          return this._EVALUATE_SINGLE_ACTION({ action, canonical, orgCanonical })
        }))
      } finally {
        this._evaluationsInFlight = Math.max(0, this._evaluationsInFlight - 1)
        this.isEvaluatingUserActions = this._evaluationsInFlight > 0
      }
    },

    async _EVALUATE_SINGLE_ACTION ({ action, canonical, orgCanonical }) {
      const entry = this.evaluatedActions[action]
      if (entry && isCacheEntryFresh(entry) && doesEntryCoverCanonical(entry, canonical)) return

      const entityCanonicals = toEntityCanonicals(canonical)
      const key = getEvaluationKey(action, entityCanonicals)
      const inFlight = this._evaluatedActionsInFlight[key]
      if (inFlight) {
        await inFlight
        return
      }

      const requestPromise = new Promise((resolve) => {
        this._evaluatedActionsPending.push({ action, entityCanonicals, resolve, key })
        this._SCHEDULE_EVALUATED_ACTIONS_FLUSH(orgCanonical)
      })

      this._evaluatedActionsInFlight[key] = requestPromise
      await requestPromise
    },

    _SCHEDULE_EVALUATED_ACTIONS_FLUSH (orgCanonical) {
      if (this._evaluatedActionsFlushHandle) return
      this._evaluatedActionsFlushHandle = setTimeout(async () => {
        await this._FLUSH_EVALUATED_ACTIONS(orgCanonical)
      }, 0)
    },

    async _FLUSH_EVALUATED_ACTIONS (orgCanonical) {
      const pending = [...this._evaluatedActionsPending]
      this._evaluatedActionsPending = []
      this._evaluatedActionsFlushHandle = null
      if (_.isEmpty(pending)) return

      const dedupedItems = _(pending)
        .uniqBy('key')
        .map(({ action, entityCanonicals, key }) => ({
          canonical: key,
          action,
          entity_canonicals: entityCanonicals,
        }))
        .value()

      let responseData = {}
      let responseErrors = null
      try {
        const { data, errors } = (await this.$cycloid.ydAPI.canDoBatch(orgCanonical, dedupedItems)) || {}
        responseData = data || {}
        responseErrors = errors || null
      } catch (error) {
        responseErrors = [error]
      }

      const evaluatedByKey = _.fromPairs(dedupedItems.map((item) => {
        const key = item.canonical
        const responseItem = responseData[key]
        const evaluated = responseItem
          ? {
              action: responseItem.action || item.action,
              ok: responseItem.ok ?? false,
              entity_canonicals: responseItem.entity_canonicals || [],
            }
          : null
        return [key, evaluated]
      }))

      for (const pendingItem of pending) {
        const fallback = { action: pendingItem.action, ok: false, entity_canonicals: [] }
        const requestedCanonicals = pendingItem.entityCanonicals
        // On a failed request, leave actions unevaluated so denial gates fail open: a
        // transient canDoBatch error must not redirect authorized users off gated routes.
        // The next evaluation retries since the key is no longer in flight.
        const evaluated = _.$isEmpty(responseErrors)
          ? evaluatedByKey[pendingItem.key] || fallback
          : null
        if (evaluated) this.SET_EVALUATED_ACTIONS({ ...evaluated, requestedCanonicals })
        pendingItem.resolve(evaluated)
        delete this._evaluatedActionsInFlight[pendingItem.key]
      }
    },

    RESTORE_CREDENTIALS () {
      // Per-tab isolation (ENGFE-512): sessionStorage is this tab's source of truth so a reload
      // keeps the tab on its own organization, even if another tab switched org meanwhile. Fall
      // back to localStorage so a freshly opened tab (or a pasted URL) seeds from the last active
      // session without forcing a re-login; SET_CREDENTIALS then claims it into sessionStorage.
      const jwt = this.jwt || sessionStorage[LSK.JWT] || localStorage[LSK.JWT]
      try {
        if (jwt && !isTokenExpired(jwt)) this.SET_CREDENTIALS({ token: jwt })
      } catch {
        // A corrupt stored token (e.g. the string "null") makes isTokenExpired throw and would
        // wedge every boot until site data is cleared; drop it and boot unauthenticated (ENG-360).
        this.CLEAR_CREDENTIALS()
      }
    },

    /** @returns {Promise<?Array>} the errors of the resend, if any */
    async RESEND_VERIFY_EMAIL (email) {
      const { errors } = (await this.$cycloid.ydAPI.resendVerifyEmail({ email })) || {}
      if (errors) {
        const { useAlertsStore } = await import('@/store/pinia/alerts')
        useAlertsStore().SHOW_ALERT({ type: 'error', content: errors })
      }
      return errors
    },

    async RETRIEVE_INVITATION_DATA (token) {
      const { data } = (await this.$cycloid.ydAPI.getPendingInvitation(token)) || {}
      if (data) this.SET_INVITATION_DATA({ token, email: data.email })
    },

    async VERIFY_EMAIL (token) {
      const { errors } = (await this.$cycloid.ydAPI.verifyEmail(token)) || {}
      if (errors) this.SET_ERRORS({ key: 'verifyEmail', errors })
    },

    async LOGIN ({ email, password, token, orgCanonical } = {}) {
      let dataLogin = null

      if (email && password) {
        this.CLEAR_AUTH_ERRORS('login')
        const { data, errors } = (await this.$cycloid.ydAPI.login({ email, password, orgCanonical })) || {}
        if (errors) {
          this.SET_ERRORS({ key: 'login', errors })
          throw new Error('Login failed')
        }
        dataLogin = data
      } else if (token) {
        const { data, errors } = (await this.$cycloid.ydAPI.emailAuthenticationVerification(token, orgCanonical)) || {}
        if (errors) {
          this.$router.push({ name: 'MFAInvalidLoginLink' }).catch(() => { /* silenced */ })
          throw new Error('Email authentication verification failed')
        }
        dataLogin = data
      }

      if (dataLogin) await this._APPLY_CREDENTIALS(dataLogin)

      const jwt = (token || email) ? this.jwt : this.sso.token
      const rootStore = useRootStore()
      await Promise.all([
        rootStore.UPDATE_USER_SESSION(jwt),
        rootStore.FETCH_ORGS(),
      ])
    },

    async _APPLY_CREDENTIALS (data) {
      this.SET_CREDENTIALS(data)
      const { useCustomersStore } = await import('@/store/pinia/customers')
      const orgStore = useOrganizationStore()
      const customersStore = useCustomersStore()

      // Seed the root scope from the freshly set token, not the org store: UPDATE_USER_SESSION
      // (which populates the org store from the JWT) only runs after this, so orgStore is still
      // empty here. An unseeded scopes[0] later makes a child-org switch refresh without a parent
      // organization, which triggers a "User not found" error and a 404 (ENG-220).
      const { canonical, name } = this.jwtDecoded?.organization ?? {}
      const scope = {
        name: name ?? orgStore.orgName,
        canonical: canonical ?? orgStore.orgCanonical,
        jwt: this.jwt,
      }
      customersStore.SET_CURRENT_SCOPE(scope)
      localStorage.setItem(LSK.APP_VERSION, __APP_VERSION__)
    },

    /**
     * Replaces the current session with another user's (ENG-408): the impersonation target, or
     * the support user's own session when impersonation stops. Everything derived from the
     * previous identity is dropped first, the customer scopes and the evaluated-actions cache
     * would otherwise leak the previous user's org and permissions into the new session.
     */
    async _SWITCH_SESSION (data) {
      const { useCustomersStore } = await import('@/store/pinia/customers')
      useCustomersStore().RESET_CUSTOMERS_STATE()
      // Like a fresh login: the new token may carry no organization (an impersonation starts
      // org-less, the target's first organization is picked afterwards), and the previous
      // identity's organization must not leak into the seeded scope
      useOrganizationStore().RESET_ORG_STATE()
      this.CLEAR_EVALUATED_ACTIONS_CACHE()
      await this._APPLY_CREDENTIALS(data)
      const rootStore = useRootStore()
      await Promise.all([
        rootStore.UPDATE_USER_SESSION(this.jwt),
        rootStore.FETCH_ORGS(),
      ])
    },

    /**
     * Starts a support impersonation session (ENG-408). The caller is logged in as themselves;
     * their own JWT is kept in sessionStorage so STOP_IMPERSONATION restores it without a
     * re-login. sessionStorage on purpose: the impersonated session must not leak to other tabs.
     * The session is org-less, like a plain login: the caller then lands wherever the target
     * would, through the usual organizations fetch and CHANGE_ORGANIZATION.
     */
    async IMPERSONATE ({ email }) {
      this.CLEAR_AUTH_ERRORS('impersonate')
      const { data, errors } = (await this.$cycloid.ydAPI.impersonateUser({ email })) || {}
      if (errors) {
        this.SET_ERRORS({ key: 'impersonate', errors })
        throw new Error('Impersonation failed')
      }
      sessionStorage.setItem(LSK.IMPERSONATION_ORIGINAL_JWT, this.jwt)
      await this._SWITCH_SESSION(data)
    },

    /**
     * Ends the impersonated session and restores the support user's own one (ENG-408).
     * Returns the route to land on. The stop endpoint only records the end for the audit log;
     * its failure is kept in errors.impersonate but never traps the support user in the
     * impersonated session. Without a restorable original JWT (another tab, or it expired
     * meanwhile) this is a plain logout.
     */
    async STOP_IMPERSONATION () {
      const originalJwt = sessionStorage.getItem(LSK.IMPERSONATION_ORIGINAL_JWT)
      sessionStorage.removeItem(LSK.IMPERSONATION_ORIGINAL_JWT)

      this.CLEAR_AUTH_ERRORS('impersonate')
      const { errors } = (await this.$cycloid.ydAPI.stopImpersonateUser()) || {}
      if (errors) {
        this.SET_ERRORS({ key: 'impersonate', errors })
        console.error('[STOP_IMPERSONATION] the stop was not recorded by the API', errors)
      }

      if (!originalJwt || isTokenExpired(originalJwt)) {
        await this.LOGOUT()
        return { name: 'login' }
      }

      // permissions and owns belong to the impersonated user; null them rather than let
      // SET_CREDENTIALS preserve them, REFRESH_TOKEN below fetches the support user's own
      await this._SWITCH_SESSION({ token: originalJwt, permissions: null, owns: null })

      const orgCanonical = this.jwtDecoded?.organization?.canonical
      if (!orgCanonical) return { name: 'organizations' }

      await this.REFRESH_TOKEN({ query: { organization_canonical: orgCanonical } })
      if (!this.isAuthenticated) return { name: 'login' }
      return { name: 'dashboard', params: { orgCanonical } }
    },

    async LOGOUT () {
      // The session is ended on the API too, so a copy of the token is refused (ENG-415). The
      // token is kept aside and the local state cleared first: callers that do not await LOGOUT
      // may log in again meanwhile, and the late API call must not touch that new session.
      const jwt = this.isAuthenticated ? this.jwt : null

      const { useUserStore } = await import('@/store/pinia/user')
      const { useOrganizationStore } = await import('@/store/pinia/organization')
      const { useCustomersStore } = await import('@/store/pinia/customers')
      const { useRootStore } = await import('@/store/pinia/root')

      const userStore = useUserStore()
      const orgStore = useOrganizationStore()
      const customersStore = useCustomersStore()
      const licenceStore = useLicenceStore()
      const rootStore = useRootStore()

      userStore.CLEAR_PROFILE()
      licenceStore.SET_FETCHED(null)
      orgStore.RESET_ORG_STATE()
      customersStore.RESET_CUSTOMERS_STATE()
      this.CLEAR_EVALUATED_ACTIONS_CACHE()
      this.CLEAR_CREDENTIALS()
      this.RESET_AUTH_STATE({ preserveKeys: ['afterLogIn'] })

      rootStore.RESET_STATE()

      localStorage.removeItem(LSK.ORGANIZATION)
      localStorage.removeItem(LSK.REDIRECT_ROUTE)
      sessionStorage.removeItem(LSK.ORGANIZATION)
      sessionStorage.removeItem(LSK.IMPERSONATION_ORIGINAL_JWT)
      sessionStorage.removeItem(NAV_HISTORY_KEY)

      // Best effort: an already revoked or unreachable session must not keep the user logged in
      if (jwt) {
        try {
          await this.$cycloid.ydAPI.logout({ token: jwt })
        } catch (error) {
          console.error('[LOGOUT] the session was not ended by the API', error)
        }
      }
    },

    async SIGNUP (user) {
      this.CLEAR_AUTH_ERRORS('signUp')

      const { awsMarketplaceToken } = this

      const body = awsMarketplaceToken ? { aws_marketplace_token: awsMarketplaceToken, ...user } : user
      const apiMethodName = awsMarketplaceToken ? 'signUpAWSMarketplace' : 'signUp'

      const { data, errors } = await this.$cycloid.ydAPI[apiMethodName](body) || {}

      if (data) this.SET_VERIFIED(data.verified)
      if (errors) this.SET_ERRORS({ key: 'signUp', errors })
    },

    async SSO_SIGNUP ({ socialProvider, user }) {
      this.CLEAR_AUTH_ERRORS('sso')
      const { errors, data } = (await this.$cycloid.ydAPI.createOAuthUser(socialProvider, user)) || {}

      if (errors) this.SET_ERRORS({ key: 'sso', errors })
      if (data) this.SET_SSO(data)
    },

    async GET_SSO_USER ({ socialProvider, ssoCode, organizationCanonical, codeVerifier }) {
      this.CLEAR_AUTH_ERRORS('sso')

      const query = {
        oauth_code: ssoCode,
        organization_canonical: organizationCanonical,
        code_verifier: codeVerifier,
      }

      const { data, errors } = (await this.$cycloid.ydAPI.getOAuthUser(socialProvider, query)) || {}

      if (errors) this.SET_ERRORS({ key: 'sso', errors })
      if (data) this.SET_SSO(data)
    },

    async HANDLE_SAML2_AUTH ({ ssoCode, socialProvider = 'saml2' }) {
      this.CLEAR_AUTH_ERRORS('sso')

      await this.GET_SSO_USER({ socialProvider, ssoCode: { oauth_code: ssoCode } })

      if (_.$isEmpty(this.jwt)) await this.SSO_SIGNUP({ socialProvider, user: this.sso.user })
      if (_.$isEmpty(this.errors.sso)) await this.LOGIN()
    },

    async RESET_PASSWORD ({ token, password }) {
      this.CLEAR_AUTH_ERRORS('resetPassword')

      const { errors } = (await this.$cycloid.ydAPI.passwordResetUpdate(token, password)) || {}

      if (!_.$isEmpty(errors)) this.SET_ERRORS({ key: 'resetPassword', errors })
    },

    SET_CREDENTIALS ({ token: jwt, permissions, owns }) {
      // Never persist a falsy token: it serialises to the string "null" and makes isTokenExpired
      // throw on the next boot. Clearing credentials goes through CLEAR_CREDENTIALS (ENG-360).
      if (!jwt) return
      this.$state['jwt'] = jwt
      // Write to both: sessionStorage is the per-tab source of truth (ENGFE-512), localStorage is
      // the shared seed that lets a new tab / pasted URL start without a re-login.
      sessionStorage.setItem(LSK.JWT, jwt)
      localStorage.setItem(LSK.JWT, jwt)

      // Preserve permissions when the caller omits it, the same way owns is preserved below:
      // RESTORE_CREDENTIALS passes the token only, and dropping the permissions there clears
      // `organization:**`, which turns isOrgAdmin false and empties the sidebar (ENG-343).
      if (permissions !== undefined) {
        this.$state['permissions'] = permissions
      }

      // Preserve owns when the caller omits it (e.g. RESTORE_CREDENTIALS passes the token only),
      // the same way the jwt above falls back to storage, so a reload keeps the owner fallback.
      if (owns !== undefined) {
        this.$state['owns'] = owns
        owns
          ? localStorage.setItem(LSK.OWNS, JSON.stringify(owns))
          : localStorage.removeItem(LSK.OWNS)
      }
    },

    SET_INVITATION_DATA ({ token, email }) {
      this.$state['invitation'] = { token, email }
    },

    CLEAR_CREDENTIALS () {
      this.$state['jwt'] = null
      sessionStorage.removeItem(LSK.JWT)
      localStorage.removeItem(LSK.JWT)
      this.$state['permissions'] = null
      this.$state['owns'] = null
      localStorage.removeItem(LSK.OWNS)
    },

    SET_DEFAULT_ROUTE (redirectRoute) {
      this.$state.afterLogIn['redirectRoute'] = redirectRoute
      localStorage.setItem(LSK.REDIRECT_ROUTE, JSON.stringify(redirectRoute))
    },

    SET_SSO (res) {
      this.$state['sso'] = res
      this.$state['jwt'] = res.token
    },

    SET_SOCIAL_PROVIDER (socialProvider) {
      this.$state['socialProvider'] = socialProvider
    },

    SET_ORG_SWITCH_IN_FLIGHT (promise) {
      this.$state._orgSwitchInFlight = promise
    },

    CLEAR_EVALUATED_ACTIONS_CACHE () {
      this.$state.evaluatedActions = {}
      this.$state._evaluatedActionsInFlight = {}
      this.$state._evaluatedActionsPending = []
      if (this._evaluatedActionsFlushHandle) {
        clearTimeout(this._evaluatedActionsFlushHandle)
      }
      this.$state._evaluatedActionsFlushHandle = null
    },

    CLEAR_EVALUATED_ACTION_CACHE (action) {
      if (_.isEmpty(action)) return
      delete this.$state.evaluatedActions[action]
    },

    SET_EVALUATED_ACTIONS ({ ok, entity_canonicals: allowedEntities, action, requestedCanonicals = [] }) {
      const evaluatedAction = _.$get(this.evaluatedActions, action, { entityCanonicals: [] })
      const entityCanonicals = _.$isEmpty(allowedEntities)
        ? []
        : _.uniq([...evaluatedAction.entityCanonicals, ...allowedEntities])
      this.$state.evaluatedActions[action] = { ok, entityCanonicals, requestedCanonicals, fetchedAt: Date.now() }
    },

    SET_AWS_MARKETPLACE_TOKEN (token) {
      this.$state['awsMarketplaceToken'] = token
    },

    SET_AWS_MARKETPLACE_SUBSCRIPTION (subscription) {
      this.$state['awsMarketplaceSubscription'] = subscription
    },

    SET_VERIFIED (verified) {
      this.$state['verified'] = verified
    },
  },
  persistedState: createPersistedState('cyAuth', {
    includePaths: ['socialProvider'],
    version: VERSION,
  }),
})
